import { useEffect } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { Button } from "@/ui";
import { authSession, syncFeed } from "@/services/client";
import { RealtimeClient } from "@/services/realtime";
import { useCurrentUser, useSignOut } from "./session";

const BRAND_MARK = "A8";

/** The root tabs. Every page is one sync feed away — the tabs only change the view. */
const TABS = [
  { to: "/", label: "Activities", end: true },
  { to: "/timeline", label: "Timeline", end: false },
  { to: "/planning", label: "Planning", end: false },
  { to: "/money", label: "Money", end: false },
  { to: "/people", label: "People", end: false },
] as const;

/**
 * The authenticated layout: brand, root tabs, account actions, and the page.
 *
 * The sync feed is started once here rather than per page, so switching tabs never
 * re-pulls the feed and every page reads the same live state.
 */
export function AppShell() {
  useEffect(() => {
    void syncFeed.start();
    // Live updates: the nudge carries the latest feed position, so a client that is
    // already caught up (e.g. after its own write) skips the pull.
    const realtime = new RealtimeClient({
      getToken: () => authSession.validAccessToken(),
      onChange: (latestSeq) => void syncFeed.pullIfBehind(latestSeq),
    });
    realtime.start();
    return () => realtime.stop();
  }, []);

  const user = useCurrentUser();
  const onSignOut = useSignOut();

  return (
    <>
      <header className="app-bar">
        <div className="app-bar-inner">
          <span className="a8-badge">{BRAND_MARK}</span>
          <nav className="tabs">
            {TABS.map((tab) => (
              <NavLink
                key={tab.to}
                to={tab.to}
                end={tab.end}
                className={({ isActive }) => (isActive ? "tab is-active" : "tab")}
              >
                {tab.label}
              </NavLink>
            ))}
          </nav>
          <div className="app-bar-account">
            {user && <span className="console-user">{user.email}</span>}
            <Link to="/settings" className="a8-link">
              Settings
            </Link>
            <Button variant="secondary" onClick={onSignOut}>
              Sign out
            </Button>
          </div>
        </div>
      </header>
      <Outlet />
    </>
  );
}
